import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Upload, X, FileText, AlertTriangle, File, Image as ImageIcon, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import PermitMap from '../../components/PermitMap';

const PermitEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [landRegistryDocument, setLandRegistryDocument] = useState(null);
  const [registryMapDocument, setRegistryMapDocument] = useState(null);
  const [files, setFiles] = useState([]);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({ landRegistry: null, registryMap: null });

  // State for reviewer selection dialog (used for Returned permits)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  // Track documents marked for removal
  const [removeLandRegistry, setRemoveLandRegistry] = useState(false);
  const [removeRegistryMap, setRemoveRegistryMap] = useState(false);
  const [removedSupportingDocs, setRemovedSupportingDocs] = useState([]);
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      father_name: '',
      village: '',
      subdivisionId: '',
      post_office: '',
      police_station: '',
      district: 'MON',
      size_sqft: '',
      issue_date: '',
      validity_date: '',
      north_boundary: '',
      south_boundary: '',
      east_boundary: '',
      west_boundary: '',
      location_description: '',
      remarks: ''
    }
  });

  // Fetch subdivisions
  const { data: subdivisionsData } = useQuery(
    'subdivisions',
    async () => {
      const response = await api.get('/subdivisions');
      return response.data.subdivisions;
    },
    {
      retry: 1,
      staleTime: 5 * 60 * 1000
    }
  );

  // Fetch existing permit data
  const { data: permitData, isLoading, error } = useQuery(
    ['permit', id],
    async () => {
      const response = await api.get(`/permits/${id}`);
      return response.data.permit;
    },
    {
      retry: 1
    }
  );

  // Fetch reviewers list (only for Returned permits when dialog is shown)
  const { data: reviewersData } = useQuery(
    'reviewers',
    async () => {
      const response = await api.get(`/users/reviewers/list?subdivisionId=${permitData?.subdivisionId}`);
      return response.data.reviewers;
    },
    {
      enabled: showSubmitDialog && !!permitData?.subdivisionId,
      retry: 1
    }
  );

  // Submit permit mutation (for Returned permits after saving)
  const submitMutation = useMutation(
    async ({ assignedLevel, assignedUser }) => {
      const response = await api.post(`/permits/${id}/submit`, { assignedLevel, assignedUser });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit', id]);
        queryClient.invalidateQueries('permits');
        toast.success('Permit saved and submitted for review successfully!');
        setShowSubmitDialog(false);
        setSelectedLevel('');
        setSelectedUser('');
        navigate(`/permits/${id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to submit permit for review');
      }
    }
  );

  // Pre-fill form when data is loaded
  useEffect(() => {
    if (permitData && subdivisionsData) {
      reset({
        name: permitData.name || '',
        father_name: permitData.father_name || '',
        village: permitData.village || '',
        subdivisionId: permitData.subdivisionId || '',
        post_office: permitData.post_office || '',
        police_station: permitData.police_station || '',
        district: permitData.district || 'MON',
        size_sqft: permitData.size_sqft || '',
        issue_date: permitData.issue_date ? permitData.issue_date.split('T')[0] : '',
        validity_date: permitData.validity_date ? permitData.validity_date.split('T')[0] : '',
        north_boundary: permitData.north_boundary || '',
        south_boundary: permitData.south_boundary || '',
        east_boundary: permitData.east_boundary || '',
        west_boundary: permitData.west_boundary || '',
        location_description: permitData.location_description || '',
        remarks: permitData.remarks || ''
      });

      // Set location coordinates if available
      if (permitData.latitude && permitData.longitude) {
        setLocation({
          lat: parseFloat(permitData.latitude),
          lng: parseFloat(permitData.longitude)
        });
      }

      // Set boundary points if available
      if (permitData.boundaryPoints && Array.isArray(permitData.boundaryPoints)) {
        setBoundaryPoints(permitData.boundaryPoints);
      }
    }
  }, [permitData, subdivisionsData, reset]);

  // Fetch pre-signed URLs for image previews when permit data loads
  useEffect(() => {
    const fetchPreviewUrls = async () => {
      if (!permitData) return;

      try {
        const urls = {
          landRegistry: null,
          registryMap: null
        };

        // Fetch land registry document preview URL if it's an image and active
        if (permitData.landRegistryDocument?.mimetype?.includes('image') &&
            permitData.landRegistryDocument?.isActive !== false) {
          const response = await api.get(`/permits/${id}/documents/download?documentType=landRegistry`);
          if (response.data.success) {
            urls.landRegistry = response.data.downloadUrl;
          }
        }

        // Fetch registry map document preview URL if it's an image and active
        if (permitData.registryMapDocument?.mimetype?.includes('image') &&
            permitData.registryMapDocument?.isActive !== false) {
          const response = await api.get(`/permits/${id}/documents/download?documentType=registryMap`);
          if (response.data.success) {
            urls.registryMap = response.data.downloadUrl;
          }
        }

        setPreviewUrls(urls);
      } catch (error) {
        console.error('Error fetching preview URLs:', error);
      }
    };

    fetchPreviewUrls();
  }, [permitData, id]);

  // Update permit mutation
  const updatePermit = useMutation(
    async (data) => {
      const formData = new FormData();

      // Add text fields
      Object.keys(data).forEach(key => {
        if (data[key]) {
          formData.append(key, data[key]);
        }
      });

      // Add boundary points if set (minimum 3 points)
      if (boundaryPoints && boundaryPoints.length >= 3) {
        formData.append('boundaryPoints', JSON.stringify(boundaryPoints));
      } else if (location.lat && location.lng) {
        // Fall back to single location if no boundary points
        formData.append('latitude', location.lat);
        formData.append('longitude', location.lng);
      }

      // Add land registry document if being replaced/updated
      if (landRegistryDocument) {
        formData.append('landRegistryDocument', landRegistryDocument);
      }

      // Add registry map document if being replaced/updated
      if (registryMapDocument) {
        formData.append('registryMapDocument', registryMapDocument);
      }

      // Add new supporting documents
      files.forEach(file => {
        formData.append('documents', file);
      });

      // Add removal flags (backend support needed for deletion)
      if (removeLandRegistry) {
        formData.append('removeLandRegistry', 'true');
      }
      if (removeRegistryMap) {
        formData.append('removeRegistryMap', 'true');
      }
      if (removedSupportingDocs.length > 0) {
        formData.append('removedSupportingDocs', JSON.stringify(removedSupportingDocs));
      }

      const response = await api.put(`/permits/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit', id]);
        queryClient.invalidateQueries('permits');
        if (permitData?.status === 'Returned') {
          toast.success('Permit saved! Now select a reviewer to submit.');
          setShowSubmitDialog(true);
        } else {
          toast.success('Permit updated successfully!');
          navigate(`/permits/${id}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update permit');
      },
    }
  );

  const handleLandRegistryChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLandRegistryDocument(file);
    }
  };

  const removeLandRegistryDocument = () => {
    setLandRegistryDocument(null);
  };

  const handleRegistryMapChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRegistryMapDocument(file);
    }
  };

  const removeRegistryMapDocument = () => {
    setRegistryMapDocument(null);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle removal of existing documents
  const handleRemoveLandRegistry = () => {
    if (window.confirm('Are you sure you want to remove this Land Registry document? This action cannot be undone.')) {
      setRemoveLandRegistry(true);
      toast.success('Land Registry document will be removed when you save changes');
    }
  };

  const handleRemoveRegistryMap = () => {
    if (window.confirm('Are you sure you want to remove this Registry Map document?')) {
      setRemoveRegistryMap(true);
      toast.success('Registry Map document will be removed when you save changes');
    }
  };

  const handleRemoveSupportingDoc = (index) => {
    if (window.confirm('Are you sure you want to remove this supporting document?')) {
      setRemovedSupportingDocs(prev => [...prev, index]);
      toast.success('Supporting document will be removed when you save changes');
    }
  };

  // Undo removal actions
  const undoRemoveLandRegistry = () => {
    setRemoveLandRegistry(false);
    toast.success('Removal cancelled');
  };

  const undoRemoveRegistryMap = () => {
    setRemoveRegistryMap(false);
    toast.success('Removal cancelled');
  };

  const undoRemoveSupportingDoc = (index) => {
    setRemovedSupportingDocs(prev => prev.filter(i => i !== index));
    toast.success('Removal cancelled');
  };

  // Get file preview - shows actual content for images, icon for PDFs
  const getFilePreview = (document, size = 'large', previewUrl = null) => {
    const iconSize = size === 'large' ? 'h-32 w-32' : 'h-24 w-24';

    if (document.mimetype?.includes('image') && previewUrl) {
      // Show actual image preview using pre-signed URL
      return (
        <div className={`${iconSize} rounded-lg overflow-hidden border-2 border-gray-200 bg-white`}>
          <img
            src={previewUrl}
            alt={document.originalName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none' }} className="w-full h-full flex items-center justify-center">
            <ImageIcon className={`${iconSize} text-blue-500`} />
          </div>
        </div>
      );
    } else if (document.mimetype?.includes('pdf')) {
      // Show PDF icon with preview indicator
      return (
        <div className="relative">
          <File className={`${iconSize} text-red-500`} />
          <div className="absolute bottom-0 right-0 bg-red-100 text-red-700 text-xs px-2 py-1 rounded">
            PDF
          </div>
        </div>
      );
    }
    // Default file icon
    return <FileText className={`${iconSize} text-gray-500`} />;
  };

  const onSubmit = (data) => {
    // Warn if boundary points are provided but incomplete (1-2 points)
    if (boundaryPoints && boundaryPoints.length > 0 && boundaryPoints.length < 3) {
      toast.error('Boundary points require at least 3 points to form a polygon. Please add more points or clear them.');
      return;
    }
    updatePermit.mutate(data);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading permit</h3>
              <p className="mt-2 text-sm text-red-700">
                {error.response?.data?.message || 'Failed to load permit details'}
              </p>
              <button
                onClick={() => navigate('/permits')}
                className="mt-3 text-sm text-red-600 hover:text-red-500"
              >
                ← Back to permits list
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check permissions
  const permit = permitData;

  // Determine if user can edit this permit
  let canEdit = false;

  if (user.role === 'dc') {
    // DC can edit any permit except Draft/Returned permits created by other users
    const isDraftOrReturned = permit.status === 'Draft' || permit.status === 'Returned';
    canEdit = !(isDraftOrReturned && permit.submittedBy !== user.id);
  } else {
    // Other roles:
    // User is the creator and permit is in Draft or Returned status
    // OR permit is at the user's approval level
    canEdit =
      (permit.submittedBy === user.id && (permit.status === 'Draft' || permit.status === 'Returned')) ||
      (user.role === 'eac' && permit.status === 'Pending_EAC_Review') ||
      (user.role === 'sdo' && permit.status === 'Pending_SDO_Review') ||
      (user.role === 'adc' && permit.status === 'Pending_ADC_Review');
  }

  if (!canEdit) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Access Denied</h3>
              <p className="mt-2 text-sm text-yellow-700">
                You don't have permission to edit this permit.
                {permit.submittedBy === user.id && permit.status !== 'Draft' && permit.status !== 'Returned' && ' You can only edit permits that are in Draft or Returned status.'}
                {permit.submittedBy !== user.id && ' This permit is not assigned to you for review/approval.'}
              </p>
              <button
                onClick={() => navigate(`/permits/${id}`)}
                className="mt-3 text-sm text-yellow-600 hover:text-yellow-500"
              >
                ← Back to permit details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/permits/${id}`)}
          className="flex items-center text-secondary-600 hover:text-secondary-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Permit Details
        </button>
        <h1 className="text-2xl font-bold text-secondary-900">Edit Permit</h1>
        <p className="text-secondary-600">Update the details for permit {permit.permit_no}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Permit Holder Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                className={`input ${errors.name ? 'input-error' : ''}`}
                placeholder="Enter full name of permit holder"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="label">Father's Name</label>
              <input
                {...register('father_name')}
                type="text"
                className="input"
                placeholder="S/O: Father's name"
              />
            </div>

            <div>
              <label className="label">Village/Ward *</label>
              <input
                {...register('village', { required: 'Village/Ward is required' })}
                type="text"
                className={`input ${errors.village ? 'input-error' : ''}`}
                placeholder="Enter village or ward name"
              />
              {errors.village && (
                <p className="mt-1 text-sm text-red-600">{errors.village.message}</p>
              )}
            </div>

            <div>
              <label className="label">Subdivision *</label>
              <select
                {...register('subdivisionId', { required: 'Subdivision is required' })}
                className={`input ${errors.subdivisionId ? 'input-error' : ''}`}
              >
                <option value="">Select Subdivision</option>
                {subdivisionsData?.map((subdivision) => (
                  <option key={subdivision.id} value={subdivision.id}>
                    {subdivision.name}
                  </option>
                ))}
              </select>
              {errors.subdivisionId && (
                <p className="mt-1 text-sm text-red-600">{errors.subdivisionId.message}</p>
              )}
            </div>

            <div>
              <label className="label">Post Office</label>
              <input
                {...register('post_office')}
                type="text"
                className="input"
                placeholder="Enter post office"
              />
            </div>

            <div>
              <label className="label">Police Station</label>
              <input
                {...register('police_station')}
                type="text"
                className="input"
                placeholder="Enter police station"
              />
            </div>

            <div>
              <label className="label">District</label>
              <input
                {...register('district')}
                type="text"
                className="input"
                placeholder="District"
              />
            </div>

            <div>
              <label className="label">Size (sq ft) *</label>
              <input
                {...register('size_sqft', { required: 'Size is required' })}
                type="text"
                className={`input ${errors.size_sqft ? 'input-error' : ''}`}
                placeholder="e.g., 55x30=1655 SQ FT"
              />
              {errors.size_sqft && (
                <p className="mt-1 text-sm text-red-600">{errors.size_sqft.message}</p>
              )}
            </div>

            {permitData?.issue_date && (
              <div>
                <label className="label">Issue Date (Set by DC on Approval)</label>
                <input
                  {...register('issue_date')}
                  type="date"
                  className="input bg-gray-50"
                  readOnly
                  disabled
                />
                <p className="mt-1 text-sm text-gray-500">Issue date is automatically set when DC approves the permit</p>
              </div>
            )}

            <div>
              <label className="label">Validity Date</label>
              <input
                {...register('validity_date')}
                type="date"
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Land Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Land Boundaries</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">North Boundary</label>
              <input
                {...register('north_boundary')}
                type="text"
                className="input"
                placeholder="e.g., QUARTER"
              />
            </div>

            <div>
              <label className="label">South Boundary</label>
              <input
                {...register('south_boundary')}
                type="text"
                className="input"
                placeholder="e.g., SHAMONG"
              />
            </div>

            <div>
              <label className="label">East Boundary</label>
              <input
                {...register('east_boundary')}
                type="text"
                className="input"
                placeholder="e.g., MANWANG"
              />
            </div>

            <div>
              <label className="label">West Boundary</label>
              <input
                {...register('west_boundary')}
                type="text"
                className="input"
                placeholder="e.g., WENGAO"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Location Description</label>
              <textarea
                {...register('location_description')}
                rows={3}
                className="input"
                placeholder="e.g., North: Soil Office, South: CS Office..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Remarks</label>
              <textarea
                {...register('remarks')}
                rows={3}
                className="input"
                placeholder="Any additional remarks or notes"
              />
            </div>

            {/* Location Map */}
            <div className="md:col-span-2">
              <label className="label">Update Permit Boundary (Optional)</label>
              <p className="text-sm text-gray-600 mb-2">
                Click on the map to add boundary points. If adding points, minimum 3 are required to create a polygon boundary.
              </p>
              <PermitMap
                latitude={location.lat}
                longitude={location.lng}
                boundaryPoints={boundaryPoints}
                onChange={(newLocation) => setLocation(newLocation)}
                onBoundaryChange={(points) => setBoundaryPoints(points)}
                editable={true}
                height="400px"
              />
              {boundaryPoints.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {boundaryPoints.length} point(s) added {boundaryPoints.length >= 3 ? '✓' : `(need ${3 - boundaryPoints.length} more to form polygon)`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Registry Documents - Side by Side with Replace Options */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            Registry Documents
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Land Registry Document */}
            <div>
              <h4 className="text-sm font-medium text-secondary-700 mb-2">
                Land Registry Document {(!permit.landRegistryDocument || permit.landRegistryDocument.isActive === false) && <span className="text-red-600">*</span>}
              </h4>

              {permit.landRegistryDocument && permit.landRegistryDocument.isActive !== false ? (
                <div className="space-y-3">
                  {!removeLandRegistry ? (
                    <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        {/* Visual Preview */}
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          {getFilePreview(permit.landRegistryDocument, 'small', previewUrls.landRegistry)}
                        </div>

                        {/* Document Info */}
                        <div className="w-full">
                          <p className="text-sm font-semibold text-secondary-900 mb-1 truncate">
                            {permit.landRegistryDocument.originalName}
                          </p>
                          <p className="text-xs text-secondary-600">
                            {(permit.landRegistryDocument.size / 1024 / 1024).toFixed(2)} MB •
                            Version {permit.landRegistryDocument.version}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="w-full flex gap-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md text-center transition-colors">
                              Replace
                            </div>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleLandRegistryChange}
                              className="sr-only"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={handleRemoveLandRegistry}
                            className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md text-center transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="text-red-600">
                          <X className="h-10 w-10 mx-auto" />
                        </div>
                        <div className="w-full">
                          <p className="text-sm font-semibold text-red-900 mb-1">
                            Marked for removal
                          </p>
                          <p className="text-xs text-red-700">
                            This document will be removed when you save changes
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={undoRemoveLandRegistry}
                          className="w-full px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md text-center transition-colors"
                        >
                          Undo Removal
                        </button>
                      </div>
                    </div>
                  )}

                  {/* New File Selected */}
                  {landRegistryDocument && (
                    <div className="bg-green-50 p-3 rounded-md border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-800 mb-1">New file selected:</p>
                          <p className="text-sm font-medium text-secondary-900 truncate">{landRegistryDocument.name}</p>
                          <p className="text-xs text-secondary-600">
                            {(landRegistryDocument.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeLandRegistryDocument}
                          className="ml-3 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-primary-300 rounded-lg p-6 bg-primary-50">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <Upload className="h-10 w-10 text-primary-600" />
                      <label className="cursor-pointer">
                        <span className="block text-sm font-medium text-secondary-900">
                          Upload land registry document
                        </span>
                        <span className="block text-xs text-secondary-600 mt-1">
                          PDF, JPEG, PNG up to 10MB
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleLandRegistryChange}
                          className="sr-only"
                        />
                        <span className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                          Choose File
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* New File Selected */}
                  {landRegistryDocument && (
                    <div className="bg-green-50 p-3 rounded-md border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-800 mb-1">File selected:</p>
                          <p className="text-sm font-medium text-secondary-900 truncate">{landRegistryDocument.name}</p>
                          <p className="text-xs text-secondary-600">
                            {(landRegistryDocument.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeLandRegistryDocument}
                          className="ml-3 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Registry Map Document */}
            <div>
              <h4 className="text-sm font-medium text-secondary-700 mb-2">
                Registry Map Document <span className="text-secondary-500">(Optional)</span>
              </h4>

              {permit.registryMapDocument && permit.registryMapDocument.isActive !== false ? (
                <div className="space-y-3">
                  {!removeRegistryMap ? (
                    <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        {/* Visual Preview */}
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          {getFilePreview(permit.registryMapDocument, 'small', previewUrls.registryMap)}
                        </div>

                        {/* Document Info */}
                        <div className="w-full">
                          <p className="text-sm font-semibold text-secondary-900 mb-1 truncate">
                            {permit.registryMapDocument.originalName}
                          </p>
                          <p className="text-xs text-secondary-600">
                            {(permit.registryMapDocument.size / 1024 / 1024).toFixed(2)} MB •
                            Version {permit.registryMapDocument.version}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="w-full flex gap-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md text-center transition-colors">
                              Replace
                            </div>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleRegistryMapChange}
                              className="sr-only"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={handleRemoveRegistryMap}
                            className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md text-center transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="text-red-600">
                          <X className="h-10 w-10 mx-auto" />
                        </div>
                        <div className="w-full">
                          <p className="text-sm font-semibold text-red-900 mb-1">
                            Marked for removal
                          </p>
                          <p className="text-xs text-red-700">
                            This document will be removed when you save changes
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={undoRemoveRegistryMap}
                          className="w-full px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md text-center transition-colors"
                        >
                          Undo Removal
                        </button>
                      </div>
                    </div>
                  )}

                  {/* New File Selected */}
                  {registryMapDocument && (
                    <div className="bg-green-50 p-3 rounded-md border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-800 mb-1">New file selected:</p>
                          <p className="text-sm font-medium text-secondary-900 truncate">{registryMapDocument.name}</p>
                          <p className="text-xs text-secondary-600">
                            {(registryMapDocument.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeRegistryMapDocument}
                          className="ml-3 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 bg-secondary-50">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <Upload className="h-10 w-10 text-secondary-400" />
                      <label className="cursor-pointer">
                        <span className="block text-sm font-medium text-secondary-900">
                          Upload registry map document
                        </span>
                        <span className="block text-xs text-secondary-600 mt-1">
                          PDF, JPEG, PNG up to 10MB
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleRegistryMapChange}
                          className="sr-only"
                        />
                        <span className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                          Choose File
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* New File Selected */}
                  {registryMapDocument && (
                    <div className="bg-green-50 p-3 rounded-md border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-green-800 mb-1">File selected:</p>
                          <p className="text-sm font-medium text-secondary-900 truncate">{registryMapDocument.name}</p>
                          <p className="text-xs text-secondary-600">
                            {(registryMapDocument.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={removeRegistryMapDocument}
                          className="ml-3 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Supporting Documents */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">
            Supporting Documents <span className="text-secondary-500">(Optional)</span>
          </h3>

          {/* Existing Supporting Documents */}
          {permit.scannedDocuments && permit.scannedDocuments.filter(doc => doc.isActive !== false).length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-secondary-700 mb-3">Existing Documents</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {permit.scannedDocuments.map((document, index) => {
                  // Skip inactive documents
                  if (document.isActive === false) return null;

                  return (
                  <div key={index}>
                    {!removedSupportingDocs.includes(index) ? (
                      <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
                        <div className="flex flex-col items-center text-center space-y-2">
                          {/* Document Icon */}
                          <div className="p-2">
                            {document.mimetype?.includes('image') ? (
                              <FileText className="h-10 w-10 text-blue-500" />
                            ) : document.mimetype?.includes('pdf') ? (
                              <File className="h-10 w-10 text-red-500" />
                            ) : (
                              <FileText className="h-10 w-10 text-gray-500" />
                            )}
                          </div>

                          {/* Document Info */}
                          <div className="w-full">
                            <p className="text-xs font-semibold text-secondary-900 truncate" title={document.originalName}>
                              {document.originalName}
                            </p>
                            <p className="text-xs text-secondary-600">
                              {(document.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveSupportingDoc(index)}
                            className="w-full px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                        <div className="flex flex-col items-center text-center space-y-2">
                          <div className="text-red-600 p-2">
                            <X className="h-10 w-10" />
                          </div>
                          <div className="w-full">
                            <p className="text-xs font-semibold text-red-900">
                              Marked for removal
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => undoRemoveSupportingDoc(index)}
                            className="w-full px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded transition-colors"
                          >
                            Undo
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Documents */}
          <div>
            <h4 className="text-sm font-medium text-secondary-700 mb-3">
              {permit.scannedDocuments && permit.scannedDocuments.filter(doc => doc.isActive !== false).length > 0 ? 'Add More Documents' : 'Upload Documents'}
            </h4>
            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 bg-secondary-50">
              <div className="flex flex-col items-center text-center space-y-3">
                <Upload className="h-10 w-10 text-secondary-400" />
                <label className="cursor-pointer">
                  <span className="block text-sm font-medium text-secondary-900">
                    Upload additional scanned documents
                  </span>
                  <span className="block text-xs text-secondary-600 mt-1">
                    PDF, JPEG, PNG files up to 10MB each
                  </span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                  <span className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                    Choose Files
                  </span>
                </label>
              </div>
            </div>

            {/* New Files Selected */}
            {files.length > 0 && (
              <div className="mt-4">
                <h5 className="text-xs font-medium text-green-800 mb-2">New files to upload ({files.length}):</h5>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-md border border-green-200">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">{file.name}</p>
                        <p className="text-xs text-secondary-600">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-3 text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/permits/${id}`)}
            className="btn btn-outline"
            disabled={updatePermit.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={updatePermit.isLoading}
          >
            {updatePermit.isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {permitData?.status === 'Returned' ? 'Saving...' : 'Updating...'}
              </div>
            ) : permitData?.status === 'Returned' ? (
              <div className="flex items-center">
                <Send className="h-4 w-4 mr-2" />
                Save & Submit for Review
              </div>
            ) : (
              'Update Permit'
            )}
          </button>
        </div>
      </form>

      {/* Reviewer Selection Dialog (shown after saving a Returned permit) */}
      {showSubmitDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">Submit Permit for Review</h3>
            <p className="text-secondary-600 mb-4">
              Your changes have been saved. Select a reviewer to submit the permit for review.
            </p>

            {/* Level Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Submit to Level <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedLevel}
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedUser('');
                }}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitMutation.isLoading}
              >
                <option value="">Select a level...</option>
                <option value="eac">EAC (Extra Assistant Commissioner)</option>
                <option value="sdo">SDO (Sub-Divisional Officer)</option>
                <option value="adc">ADC (Additional Deputy Commissioner)</option>
                <option value="dc">DC (Deputy Commissioner)</option>
              </select>
            </div>

            {/* User Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Assign to User <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={submitMutation.isLoading || !selectedLevel}
              >
                <option value="">
                  {selectedLevel ? 'Select a user...' : 'Please select a level first'}
                </option>
                {selectedLevel && reviewersData?.filter(r => r.role === selectedLevel).map((reviewer) => (
                  <option key={reviewer.id} value={reviewer.id}>
                    {reviewer.fullName} - {reviewer.designation || reviewer.role.toUpperCase()}
                  </option>
                ))}
              </select>
              {selectedLevel && reviewersData?.filter(r => r.role === selectedLevel).length === 0 && (
                <p className="mt-1 text-sm text-red-500">
                  No users found for the selected level
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSubmitDialog(false);
                  setSelectedLevel('');
                  setSelectedUser('');
                  navigate(`/permits/${id}`);
                }}
                className="btn btn-outline"
                disabled={submitMutation.isLoading}
              >
                Skip (Submit Later)
              </button>
              <button
                onClick={() => {
                  if (!selectedLevel || !selectedUser) {
                    toast.error('Please select both a level and a user');
                    return;
                  }
                  submitMutation.mutate({ assignedLevel: selectedLevel, assignedUser: selectedUser });
                }}
                className="btn btn-primary"
                disabled={submitMutation.isLoading || !selectedLevel || !selectedUser}
              >
                {submitMutation.isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Send className="h-4 w-4 mr-2" />
                    Submit for Review
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermitEdit;
